import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ProblemError, hasGrant, usePrincipal } from "@csa/web-foundation";
import { usePartySearch, type PartyHit } from "../household/use-household";
import { useT } from "../../i18n/index";
import type { MessageKey } from "../../i18n/dictionaries";
import { Icon } from "../../ui/icons";
import {
  LINE_ASSET_TYPE,
  useCoverageConcepts,
  useCreateRecommendation,
  useHouseholdAssets,
  usePendingResponses,
  type HouseholdAsset,
} from "./use-recommendation";
import { RESPONSE_LABEL_KEY, responseDate } from "./response-labels";
import { useOpenLines } from "../catalog/use-open-lines";
import type { LineCode } from "./types";

// Öneri redesign (cto-task-secondary-surfaces §1) — the DRAFT / needs-assessment
// capture step. It gathers a household (picker, own-scope), the must-have/priority
// coverage concepts (chips from the canonical BE-1 list), the branch and the budget
// stance, then creates the recommendation → drives the flow at /recommendations/:id.
// It captures NEEDS ONLY — no score, no price is computed here (both are server-side).

// FINDING-7 — the branch list is DATA, not a literal.
//
// This surface used to hardcode ["trafik","kasko"] while phone intake offered
// five, web intake five, and prepare-offers seven — four screens, four answers
// to "which branch may I quote?", none of them the system's. The catalog defines
// seven lines and the seed authorises all of them for all three pilot carriers,
// so the narrow list was a UI opinion overriding the panel.
//
// It now comes from GET /v1/distribution/open-lines, which applies the same
// three legs as the offer gate. When the panel opens a line, this picker gains
// it with no code change — which is the requirement that mattered.
type RecLine = LineCode;

/** The seven codes the console has labels + asset mappings for. A line the
 *  catalog adds LATER shows up in the read but has no label yet, so it is
 *  filtered here rather than rendered as a raw kebab code. */
const KNOWN_LINES = new Set<LineCode>([
  "trafik",
  "kasko",
  "dask",
  "konut",
  "tss",
  "oss",
  "seyahat",
]);

// Budget stance — stable, language-neutral codes persisted in the needs_assessment
// jsonb (budgetPosture). The advisor picks a posture; the SERVER weighs price-value.
type BudgetStance = "cheapest" | "balanced" | "best_protection";
const BUDGET_STANCES: { code: BudgetStance; labelKey: MessageKey }[] = [
  { code: "cheapest", labelKey: "rec.budget.cheapest" },
  { code: "balanced", labelKey: "rec.budget.balanced" },
  { code: "best_protection", labelKey: "rec.budget.bestProtection" },
];

// The picked household — own-scope party-find hit (householdId is the create key,
// partyId a soft-ref) or a context auto-fill arriving from Household 360 / Kuyruk.
interface HouseholdSel {
  householdId: string;
  partyId?: string;
  label: string;
}

export function RecommendationHome() {
  const { t } = useT();

  return (
    <section className="surface">
      <header className="surface__head">
        <div>
          <h1 className="surface__title">{t("rec.heading")}</h1>
          <p className="surface__subtitle">{t("rec.needs.subtitle")}</p>
        </div>
      </header>

      <PendingResponsesWorklist />
      <NeedsAssessmentForm />
    </section>
  );
}

function NeedsAssessmentForm() {
  const { t } = useT();
  const { me } = usePrincipal();
  const navigate = useNavigate();
  const create = useCreateRecommendation();

  // Auto-fill from context — Household 360 / Kuyruk hand off a household via the
  // ?householdId= query param or router navigation state; don't force a re-pick.
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const ctxState = (location.state ?? {}) as { householdId?: string; householdName?: string };
  const ctxHouseholdId = searchParams.get("householdId") ?? ctxState.householdId ?? null;
  const ctxLabel = searchParams.get("name") ?? ctxState.householdName ?? null;

  const [household, setHousehold] = useState<HouseholdSel | null>(
    ctxHouseholdId ? { householdId: ctxHouseholdId, label: ctxLabel ?? ctxHouseholdId } : null,
  );
  // Closed lines are SHOWN, disabled, with their reason — hiding them teaches
  // the advisor the product does not exist; showing them teaches what would have
  // to change. `open` drives selectability, never visibility.
  const openLines = useOpenLines();
  const lines = useMemo(
    () =>
      // Array.isArray, not `?? []`: a non-array body (an error shape, a fixture's
      // catch-all `{}`) is truthy and would reach .filter.
      Array.isArray(openLines.data)
        ? openLines.data.filter((l) => KNOWN_LINES.has(l.lineCode as LineCode))
        : [],
    [openLines.data],
  );
  const firstOpen = lines.find((l) => l.open)?.lineCode as RecLine | undefined;
  const [picked, setPicked] = useState<RecLine | null>(null);
  // Default to the first OPEN line once the catalog answers — never to a
  // hardcoded "kasko" that may not be quotable in this org. NULL until the read
  // lands, so nothing downstream assumes a branch that was never offered.
  const line: RecLine | null = picked ?? firstOpen ?? null;
  const [assetId, setAssetId] = useState<string>("");
  const [mustHaves, setMustHaves] = useState<string[]>([]);
  const [priorities, setPriorities] = useState<string[]>([]);
  const [budget, setBudget] = useState<BudgetStance | "">("");

  // D2 — the household's insurable assets, narrowed to what this line insures.
  // Without one the recommendation can be created but NEVER issued, so this is
  // required whenever the household actually has a matching asset.
  const assets = useHouseholdAssets(household?.householdId ?? null);
  const wantedType = line ? LINE_ASSET_TYPE[line] : null;
  const lineAssets = useMemo(
    () => (wantedType ? (assets.data ?? []).filter((a) => a.assetType === wantedType) : []),
    [assets.data, wantedType],
  );
  // Drop a stale pick when the line (and therefore the asset type) changes.
  const pickedAsset = lineAssets.find((a) => a.id === assetId) ?? null;
  const assetRequired = lineAssets.length > 0;
  const assetSatisfied = !assetRequired || pickedAsset !== null;

  // Coverage-concept chip source (BE-1), catalog:read gated. Filter to the selected
  // line — concepts without a lineCode are line-agnostic and always offered.
  const canReadCatalog = hasGrant(me, "catalog:read");
  const concepts = useCoverageConcepts(canReadCatalog);
  const lineConcepts = useMemo(
    () => (concepts.data ?? []).filter((c) => !c.lineCode || c.lineCode === line),
    [concepts.data, line],
  );

  const toggle = (
    code: string,
    set: React.Dispatch<React.SetStateAction<string[]>>,
    other: string[],
    clearOther: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    set((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
    // A concept is a must-have OR a priority, never both — keep the two sets disjoint.
    if (other.includes(code)) clearOther((prev) => prev.filter((c) => c !== code));
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    // No line → nothing to create. Before the catalog read lands there is no
    // branch selected, and guessing one would create a recommendation on a line
    // the advisor never chose.
    if (!household || !line || create.isPending || !assetSatisfied) return;
    create.mutate(
      {
        householdId: household.householdId,
        lineCode: line,
        ...(household.partyId ? { partyId: household.partyId } : {}),
        // D2 — carry the insured asset. The issuance create schema REQUIRES it and
        // there is no path to set it afterwards, so omitting it here is what made
        // Öneri-native recommendations permanently un-issuable.
        ...(pickedAsset ? { insuredAssetId: pickedAsset.id } : {}),
        needsAssessment: {
          mustHaves,
          ...(priorities.length ? { priorities } : {}),
          ...(budget ? { budgetPosture: budget } : {}),
        },
      },
      { onSuccess: (rec) => navigate(`/recommendations/${rec.id}`) },
    );
  };

  return (
    <form className="rec-create-form k-card" onSubmit={submit} data-testid="needs-form">
      {/* Suggest-not-decide — this step gathers needs; the server suggests, the advisor decides. */}
      <div className="k-note" data-testid="rec-suggest-note">
        <div className="k-note__row">
          <span className="k-note__icon">
            <Icon name="info" size={18} />
          </span>
          <div>
            <p className="k-note__title k-note__title--sm">{t("rec.needs.suggestTitle")}</p>
            <p className="k-note__desc">{t("rec.needs.suggestBody")}</p>
          </div>
        </div>
      </div>

      {/* 1 — Household picker (own-scope party find), or the context auto-fill. */}
      <fieldset className="rec-field-group">
        <legend className="k-field__label">{t("rec.household.label")}</legend>
        <p className="k-field__hint">{t("rec.household.hint")}</p>
        <HouseholdPicker
          selected={household}
          onSelect={setHousehold}
          onClear={() => setHousehold(null)}
        />
      </fieldset>

      {/* 2 — Branch. */}
      <fieldset className="rec-field-group">
        <legend className="k-field__label">{t("rec.lineCode")}</legend>
        <p className="k-field__hint">{t("rec.line.hint")}</p>
        <div
          className="qf-seg"
          role="group"
          aria-label={t("rec.lineCode")}
          data-testid="rec-line-seg"
        >
          {lines.map((l) => {
            const code = l.lineCode as RecLine;
            return (
              <button
                key={code}
                type="button"
                className={`qf-seg__btn${code === line ? " qf-seg__btn--active" : ""}`}
                aria-pressed={code === line}
                disabled={!l.open}
                title={l.open ? undefined : t("rec.line.closedWhy")}
                data-testid={`rec-line-${code}`}
                data-open={l.open ? "true" : "false"}
                onClick={() => setPicked(code)}
              >
                {t(`rec.line.${code}` as MessageKey)}
                {!l.open && <span className="qf-seg__note">{t("rec.line.closed")}</span>}
              </button>
            );
          })}
        </div>
      </fieldset>

      {/* 2b — Insured asset (D2). Offered only once a household is picked; the set
          is narrowed to the asset type this line insures (the API re-enforces). */}
      {household && line && (
        <AssetPicker
          line={line}
          assets={lineAssets}
          loading={assets.isLoading}
          failed={Boolean(assets.error)}
          selectedId={assetId}
          onSelect={setAssetId}
        />
      )}

      {/* 3 — Must-haves (hard-gate) coverage-concept chips. */}
      <ConceptChips
        legend={t("rec.mustHaves")}
        hint={t("rec.mustHaves.hint")}
        testid="rec-musthaves"
        concepts={lineConcepts}
        selected={mustHaves}
        loading={concepts.isLoading}
        canRead={canReadCatalog}
        onToggle={(code) => toggle(code, setMustHaves, priorities, setPriorities)}
      />

      {/* 4 — Priorities (soft) coverage-concept chips. */}
      <ConceptChips
        legend={t("rec.priorities")}
        hint={t("rec.priorities.hint")}
        testid="rec-priorities"
        concepts={lineConcepts}
        selected={priorities}
        loading={concepts.isLoading}
        canRead={canReadCatalog}
        onToggle={(code) => toggle(code, setPriorities, mustHaves, setMustHaves)}
      />

      {/* 5 — Budget stance (segmented). */}
      <fieldset className="rec-field-group">
        <legend className="k-field__label">{t("rec.budget")}</legend>
        <p className="k-field__hint">{t("rec.budget.hint")}</p>
        <div
          className="qf-seg"
          role="group"
          aria-label={t("rec.budget")}
          data-testid="rec-budget-seg"
        >
          {BUDGET_STANCES.map((s) => (
            <button
              key={s.code}
              type="button"
              className={`qf-seg__btn${s.code === budget ? " qf-seg__btn--active" : ""}`}
              aria-pressed={s.code === budget}
              data-testid={`rec-budget-${s.code}`}
              onClick={() => setBudget((prev) => (prev === s.code ? "" : s.code))}
            >
              {t(s.labelKey)}
            </button>
          ))}
        </div>
      </fieldset>

      {create.error instanceof ProblemError && (
        <p role="alert" className="k-field__error">
          {create.error.problem.detail}
        </p>
      )}
      <button
        type="submit"
        className="k-btn k-btn--primary"
        data-testid="rec-start"
        disabled={!household || !line || create.isPending || !assetSatisfied}
      >
        <Icon name="arrow-right" size={16} />
        {t("rec.start")}
      </button>
      {household && assetRequired && !pickedAsset && (
        <p className="k-field__hint" data-testid="rec-asset-required">
          {t("rec.asset.required")}
        </p>
      )}
    </form>
  );
}

// D2 — the insured-asset picker. The asset is the ONE field that decides whether
// this recommendation can ever become a policy: the issuance create schema
// requires insuredAssetId, and the recommendation carries no path to set it after
// create. The Öneri form never sent it, so every recommendation started here
// ended at a permanently disabled "Poliçeleştir" — the customer said yes and the
// console could not act.
//
// Three honest states rather than one silent omission:
//   • assets exist → pick one (required; submit stays disabled until then);
//   • none for this line → say so, and point at the Teklif Formu, which is the
//     surface that actually registers a vehicle/dwelling. The draft can still be
//     created for needs capture, but the screen states plainly that it will not
//     be issuable;
//   • the read failed → say that, rather than silently behaving like "none".
function AssetPicker({
  line,
  assets,
  loading,
  failed,
  selectedId,
  onSelect,
}: {
  line: string;
  assets: HouseholdAsset[];
  loading: boolean;
  failed: boolean;
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  const { t } = useT();

  const describe = (a: HouseholdAsset): string => {
    const bits = [a.label, a.plate, a.dwellingAddress].filter(Boolean);
    return bits.length ? bits.join(" · ") : a.assetType;
  };

  return (
    <fieldset className="rec-field-group" data-testid="rec-asset-group">
      <legend className="k-field__label">{t("rec.asset.label")}</legend>
      <p className="k-field__hint">{t("rec.asset.hint")}</p>

      {loading ? (
        <p className="k-field__hint">{t("rec.asset.loading")}</p>
      ) : failed ? (
        <p className="k-field__hint" data-testid="rec-asset-error">
          {t("rec.asset.error")}
        </p>
      ) : assets.length === 0 ? (
        <div className="k-note" data-testid="rec-asset-none">
          <div className="k-note__row">
            <span className="k-note__icon">
              <Icon name="info" size={18} />
            </span>
            <div>
              <p className="k-note__desc">{t("rec.asset.none")}</p>
              <Link
                className="k-btn k-btn--secondary k-btn--sm"
                data-testid="rec-asset-to-quote"
                to={`/musteri/teklif?line=${line}`}
              >
                {t("rec.asset.toQuoteForm")}
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="rec-chips" role="group" aria-label={t("rec.asset.label")}>
          {assets.map((a) => {
            const on = a.id === selectedId;
            return (
              <button
                key={a.id}
                type="button"
                className={`rec-chip${on ? " rec-chip--on" : ""}`}
                aria-pressed={on}
                data-testid={`rec-asset-${a.id}`}
                onClick={() => onSelect(on ? "" : a.id)}
              >
                {on ? <Icon name="check" size={14} /> : null}
                {describe(a)}
                <span className="muted"> · {a.ownerName}</span>
              </button>
            );
          })}
        </div>
      )}
    </fieldset>
  );
}

// The own-scope household picker — type-to-search over GET /v1/parties/find
// (usePartySearch, the same read Müşterilerim / the quote form use). Only hits that
// carry a householdId are pickable (a party without one can't yet key a recommendation).
function HouseholdPicker({
  selected,
  onSelect,
  onClear,
}: {
  selected: HouseholdSel | null;
  onSelect: (h: HouseholdSel) => void;
  onClear: () => void;
}) {
  const { t } = useT();
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  // Auto-detect: a query starting with + or a digit is a phone; otherwise a name (U1 pattern).
  const mode: "phone" | "name" = /^[+\d]/.test(submitted.trim()) ? "phone" : "name";
  const search = usePartySearch(mode, submitted);
  const hits = search.data ?? [];

  if (selected) {
    return (
      <div className="rec-household-selected" data-testid="rec-household-selected">
        <span className="k-chip">
          <Icon name="household" size={15} />
          {selected.label}
        </span>
        <button
          type="button"
          className="k-btn k-btn--ghost k-btn--sm"
          data-testid="rec-household-change"
          onClick={onClear}
        >
          {t("rec.household.change")}
        </button>
      </div>
    );
  }

  const pick = (h: PartyHit) => {
    if (!h.householdId) return;
    onSelect({
      householdId: h.householdId,
      partyId: h.id,
      label: `${h.firstName} ${h.lastName}`.trim(),
    });
  };

  return (
    <div data-testid="rec-household-picker">
      <div className="rec-household-search">
        <label className="k-search" style={{ flex: 1 }}>
          <Icon name="search" size={18} />
          <input
            aria-label={t("rec.household.search")}
            data-testid="rec-household-input"
            placeholder={t("rec.household.placeholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                setSubmitted(query);
              }
            }}
          />
        </label>
        <button
          type="button"
          className="k-btn k-btn--secondary"
          data-testid="rec-household-search"
          disabled={!query.trim()}
          onClick={() => setSubmitted(query)}
        >
          {t("rec.household.go")}
        </button>
      </div>
      {submitted.trim() ? (
        <div className="qf-reg-results" data-testid="rec-household-results">
          {search.isLoading ? <p className="k-field__hint">{t("rec.household.loading")}</p> : null}
          {!search.isLoading && (search.error || hits.length === 0) ? (
            <p className="k-field__hint" data-testid="rec-household-none">
              {t("rec.household.none")}
            </p>
          ) : (
            <ul className="rec-household-hits">
              {hits.map((h) => {
                const name = `${h.firstName} ${h.lastName}`.trim();
                const canPick = Boolean(h.householdId);
                return (
                  <li key={h.id} className="k-card k-card--pad-sm">
                    <span className="k-avatar">
                      {(h.firstName?.[0] ?? "") + (h.lastName?.[0] ?? "")}
                    </span>
                    <span style={{ fontWeight: 600, color: "var(--k-ink)" }}>{name}</span>
                    {h.phone ? (
                      <span style={{ fontSize: 12.5, color: "var(--k-muted)" }}> · {h.phone}</span>
                    ) : null}
                    {canPick ? (
                      <button
                        type="button"
                        className="k-btn k-btn--primary k-btn--sm"
                        style={{ marginLeft: "auto" }}
                        data-testid={`rec-household-pick-${h.id}`}
                        onClick={() => pick(h)}
                      >
                        {t("rec.household.select")}
                      </button>
                    ) : (
                      <span
                        className="k-badge k-badge--warning"
                        style={{ marginLeft: "auto" }}
                        data-testid={`rec-household-nohh-${h.id}`}
                      >
                        {t("rec.household.noHousehold")}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

// A line-filtered coverage-concept chip group (multi-select). The chips ARE the
// canonical BE-1 vocabulary — the advisor selects, never free-types, so the codes
// always resolve to real coverage-equivalence concepts server-side.
function ConceptChips({
  legend,
  hint,
  testid,
  concepts,
  selected,
  loading,
  canRead,
  onToggle,
}: {
  legend: string;
  hint: string;
  testid: string;
  concepts: { code: string; label: string }[];
  selected: string[];
  loading: boolean;
  canRead: boolean;
  onToggle: (code: string) => void;
}) {
  const { t } = useT();
  return (
    <fieldset className="rec-field-group">
      <legend className="k-field__label">{legend}</legend>
      <p className="k-field__hint">{hint}</p>
      {!canRead ? (
        <p className="k-field__hint" data-testid={`${testid}-locked`}>
          {t("rec.concepts.locked")}
        </p>
      ) : loading ? (
        <p className="k-field__hint">{t("rec.concepts.loading")}</p>
      ) : concepts.length === 0 ? (
        <p className="k-field__hint" data-testid={`${testid}-empty`}>
          {t("rec.concepts.none")}
        </p>
      ) : (
        <div className="rec-chips" role="group" aria-label={legend} data-testid={testid}>
          {concepts.map((c) => {
            const on = selected.includes(c.code);
            return (
              <button
                key={c.code}
                type="button"
                className={`rec-chip${on ? " rec-chip--on" : ""}`}
                aria-pressed={on}
                data-testid={`${testid}-chip-${c.code}`}
                onClick={() => onToggle(c.code)}
              >
                {on ? <Icon name="check" size={14} /> : null}
                {c.label}
              </button>
            );
          })}
        </div>
      )}
    </fieldset>
  );
}

// I2b — "responses awaiting you": the responded-but-unbound recs (usePendingResponses).
// RBAC is UX-only (the API enforces recommendation:read/own); each item links to its
// detail, where the advisor sees the response and binds. The list empties as they bind.
function PendingResponsesWorklist() {
  const { t } = useT();
  const { me } = usePrincipal();
  const pending = usePendingResponses();

  if (!hasGrant(me, "recommendation:read", "own")) return null;
  const items = pending.data ?? [];

  return (
    <div className="rec-pending k-card" data-testid="pending-responses">
      <p className="surface__section-label" style={{ marginTop: 0 }}>
        {t("rec.pending.heading")}
        {pending.data ? ` (${items.length})` : ""}
      </p>
      {pending.error instanceof ProblemError && (
        <p role="alert" className="k-field__error">
          {pending.error.problem.detail}
        </p>
      )}
      {pending.data && items.length === 0 && (
        <p className="muted" data-testid="pending-empty">
          {t("rec.pending.empty")}
        </p>
      )}
      <ul className="rec-pending-list" style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {items.map((p) => (
          <li key={p.recommendation.id} data-testid="pending-item" style={{ marginTop: 10 }}>
            <Link
              to={`/recommendations/${p.recommendation.id}`}
              className="k-chip"
              style={{ textDecoration: "none" }}
            >
              <Icon name="conversation" size={15} />
              {p.recommendation.lineCode} ·{" "}
              <strong>{t(RESPONSE_LABEL_KEY[p.latestResponse.response])}</strong> ·{" "}
              {responseDate(p.latestResponse.respondedAt)}
              <Icon name="arrow-right" size={15} />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
